import styled, { css } from 'styled-components'
import { media } from '../../../utils/mediaQuery'

export const DrawingToolsContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$toolbar',
})<{ $toolbar?: boolean }>`
  position: absolute;
  /* The toolbar anchors to the left edge, in line with the view toggle. The FAB stays centered
     in the visible map. --popety-map-side-offset is the part of a right-side detail panel
     still overlapping the map. */
  bottom: 10px;
  left: ${({ $toolbar }) =>
    $toolbar ? '10px' : 'calc((100% - var(--popety-map-side-offset, 0px)) / 2)'};
  transform: ${({ $toolbar }) => ($toolbar ? 'none' : 'translate(-50%, -50%)')};
  z-index: 5;

  ${({ $toolbar }) =>
    $toolbar &&
    css`
      /* Desktop and up centre the toolbar in the visible map, as the FAB is. A legend reserves
         --popety-legend-clearance on the left, so the toolbar starts after it instead of under it.
         The grid's side tracks split the free space evenly, so the toolbar stays centred unless
         the clearance is larger than the left half. Smaller screens keep it at the left edge. */
      ${media.desktop} {
        left: 0;
        right: var(--popety-map-side-offset, 0px);
        --legend-clearance: var(--popety-legend-clearance, 0px);
        display: grid;
        grid-template-columns: minmax(var(--legend-clearance), 1fr) auto 1fr;
        transform: none;
        pointer-events: none;

        > * {
          grid-column: 2;
          pointer-events: auto;
        }
      }
    `}
`

export const DrawingToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  box-shadow: 0 4px 16px -1px rgba(0, 0, 0, 0.1);
`

export const DrawingToolbarButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== '$active',
})<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 10px;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  background: ${({ theme, $active }) => ($active ? theme.colors.primaryLight : 'transparent')};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.primary)};
  opacity: ${({ $active, disabled }) => ($active ? 1 : disabled ? 0.2 : 0.5)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition:
    background ${({ theme }) => theme.transitions.fast},
    opacity ${({ theme }) => theme.transitions.fast};

  /* aria-disabled keeps the button hoverable so its tooltip can explain the state. */
  &[aria-disabled='true'] {
    opacity: 0.2;
    cursor: not-allowed;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled):not([aria-disabled='true']) {
      background: ${({ $active, theme }) =>
        $active ? 'rgba(18, 193, 253, 0.18)' : theme.colors.background.tertiary};
      opacity: 1;
    }
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.border.focus};
    outline-offset: 2px;
  }

  svg {
    display: block;
    flex-shrink: 0;
  }
`

export const DrawingToolbarLabel = styled.span``

/** Icon plus visible label, for toolbar actions such as "Create alert" (see showLabel). */
export const DrawingToolbarLabelButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  padding: 8px 12px;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  white-space: nowrap;
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};

  /* aria-disabled keeps the button hoverable so its tooltip can explain the state. */
  &[aria-disabled='true'] {
    color: ${({ theme }) => theme.colors.text.tertiary};
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover:not([aria-disabled='true']) {
      background: ${({ theme }) => theme.colors.background.tertiary};
    }
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.border.focus};
    outline-offset: 2px;
  }

  svg {
    display: block;
    flex-shrink: 0;
  }

  /* Phones keep the icon only, the same as the building permits create-alert button. */
  @media (max-width: 768px) {
    padding: 8px 10px;

    ${DrawingToolbarLabel} {
      display: none;
    }
  }
`

export const DrawingToolsFAB = styled.button.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{
  $active?: boolean
  $position?: {
    bottom?: string
    left?: string
    right?: string
  }
  $zIndex?: number
}>`
  position: relative;
  width: 30px;
  height: 30px;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  border: none;
  border-radius: 24px;
  box-shadow: 0 2px 8px rgba(2, 148, 223, 0.3);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: ${({ $zIndex }) => $zIndex || 5};
  transform: ${({ $active }) => ($active ? 'rotate(45deg)' : 'rotate(0deg)')};

  &:hover {
    box-shadow: 0 4px 12px rgba(2, 148, 223, 0.4);
  }

  &:active {
    transform: scale(0.95) ${({ $active }) => ($active ? 'rotate(45deg)' : 'rotate(0deg)')};
  }

  .icon,
  svg {
    width: 16px;
    height: 16px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
  }
`

export const DrawingToolsPanel = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $active?: boolean }>`
  position: absolute;
  bottom: 56px;
  left: 50%;
  translate: -50% 0;
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: 16px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: 8px;
  display: ${({ $active }) => ($active ? 'flex' : 'none')};
  flex-direction: column;
  gap: 4px;
  min-width: 180px;
  transform-origin: bottom center;
  animation: ${({ $active }) => ($active ? 'popIn 0.2s cubic-bezier(0.4, 0, 0.2, 1)' : 'none')};
  z-index: 5;

  @keyframes popIn {
    from {
      opacity: 0;
      transform: scale(0.8) translateY(10px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  @media (max-width: 768px) {
    min-width: 160px;
    max-width: calc(100vw - 32px);
  }
`

export const DrawingToolButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $active?: boolean; $variant?: 'default' | 'delete'; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s;
  white-space: nowrap;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  color: ${({ $variant, theme }) =>
    $variant === 'delete' ? '#ef4444' : theme.colors.text.primary};

  &:hover:not(:disabled) {
    background: ${({ $variant, theme }) =>
      $variant === 'delete' ? 'rgba(239, 68, 68, 0.1)' : theme.colors.background.secondary};
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.colors.primaryLight};
      color: ${theme.colors.primary};
    `}

  .icon,
  svg {
    width: 16px;
    height: 16px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: ${({ theme }) => theme.typography.fontSize.micro};

    .icon,
    svg {
      width: 14px;
      height: 14px;
    }
  }
`

export const DrawToolsDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border.light};
  margin: 4px 8px;
`
